import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  RefreshCw,
  AlertTriangle,
  BarChart3,
  Save,
  Info,
} from 'lucide-react';

import UnifiedReportButton from '../ReportGenerator/UnifiedReportButton';

import useAnalysisStore from '../../stores/useAnalysisStore';

import RiskScenarioListView from './ActiviteA/RiskScenarioListView';
import ImplementationChecklist from './ImplementationChecklist/ImplementationChecklist';
import GuideModal from './GuideModal';
import useAtelier5Store from '../../stores/useAtelier5Store';

const Atelier5Container = ({ activeTab }) => {
  const { t } = useTranslation();

  const analysisId = useAnalysisStore((s) => s.currentAnalysis?.id);

  const synthesisData = useAtelier5Store((s) => s.synthesisData);
  const isLoading = useAtelier5Store((s) => s.isLoading);
  const error = useAtelier5Store((s) => s.error);
  const isDataLoaded = useAtelier5Store((s) => s.isDataLoaded);
  const lastUpdated = useAtelier5Store((s) => s.lastUpdated);
  const loadData = useAtelier5Store((s) => s.loadData);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      if (isMounted && analysisId) {
        await loadData(analysisId);
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, [loadData, analysisId]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!isLoading) useAtelier5Store.getState().saveRiskScenarioTab();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isLoading]);

  if (isLoading && !isDataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">
            {t('workshop5.messages.loading', 'Loading Workshop 5 data...')}
          </p>
        </div>
      </div>
    );
  }

  if (error && !isDataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('workshop5.messages.loadError', 'Loading Error')}
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              loadData();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('workshop5.buttons.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center text-sm text-slate-500 mb-2">
                <span className="hover:text-blue-600 transition-colors cursor-pointer">
                  {t('workshop5.header.breadcrumb', 'Workshop 5')}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mx-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span className="text-blue-600 font-medium">
                  {t('workshop5.header.title', 'Risk Treatment')}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center">
                <BarChart3 size={24} className="mr-3 text-blue-600" />
                {t('workshop5.header.subtitle', 'Risk Summary and Treatment')}
              </h1>
              <p className="text-slate-600 text-sm mt-1">
                {t('workshop5.synthesis.subtitle', 'Analysis and treatment of identified risks')}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => useAtelier5Store.getState().saveRiskScenarioTab()}
                disabled={isLoading}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  isLoading
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 shadow-sm hover:shadow-md'
                }`}
              >
                <Save size={16} className="mr-2" />
                {isLoading ? t('workshop5.status.saving', 'Saving...') : t('workshop5.buttons.save', 'Save')}
              </button>

              <UnifiedReportButton
                analysisId={analysisId || '67f2e2a0a5ad300283807d49'}
                buttonText="Exporter le Rapport"
                variant="outline"
                size="sm"
              />

              <button
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                onClick={() => setShowGuide(true)}
                title={t('workshop5.buttons.helpTooltip', 'Help and documentation')}
              >
                <Info size={16} className="mr-2" />
                {t('workshop5.buttons.help', 'Help')}
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-3 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-6">
              <div className="flex items-center text-slate-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span>
                  {t('workshop5.status.servicesAvailable', 'Services: All available')}
                </span>
              </div>
              {lastUpdated && (
                <div className="text-slate-500">
                  {t('workshop5.status.lastUpdated', 'Last updated')}: {new Date(lastUpdated).toLocaleString()}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-4">
              {isLoading && (
                <div className="flex items-center text-blue-600">
                  <RefreshCw size={14} className="mr-2 animate-spin" />
                  <span>{t('workshop5.status.loading', 'Loading...')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <GuideModal isOpen={showGuide} onClose={() => setShowGuide(false)} />

      <motion.div
        key="risk-scenarios"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'atelier5-implementation-checklist' ? (
          <ImplementationChecklist
            analysisId={analysisId}
            loading={isLoading}
          />
        ) : (
          <RiskScenarioListView
            strategicScenarios={synthesisData?.strategicScenarios || []}
            operationalScenarios={synthesisData?.operationalScenarios || []}
          />
        )}
      </motion.div>
    </div>
    );
  };

export default Atelier5Container;